import React from 'react';
import { useToken } from '@gluestack-ui/themed';
import { createMaterialTopTabNavigator, MaterialTopTabNavigationOptions } from '@react-navigation/material-top-tabs';
import {
  NavigationContainer,
  NavigationIndependentTree,
  type NavigationContainerRef,
} from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import type { Task } from '@/common/api/models/task';
import type { ModuleLecturerResponse } from '@/common/api/models/moduleLecturerResponse';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
// MCQ and OralExam tabs follow the saved task type. Both types are generally
// available in the TaskBaseForm dropdown (lecturer-mcq was retired).
import TaskBaseForm from './TaskBaseForm/TaskBaseForm';
import TaskResourceForm from './TaskResourceForm/TaskResourceForm';
import TaskEvalForm from './TaskEvalForm/TaskEvalForm';
import GradingRubricForm from './GradingRubricForm/GradingRubricForm';
import { MCQConfigForm } from './MCQConfigForm/MCQConfigForm';
import { OralExamConfigForm } from './OralExamConfigForm/OralExamConfigForm';
import SaveRequiredPlaceholder from './SaveRequiredPlaceholder';
import {
  TASK_EDITOR_GRADING_RUBRIC_FEATURE_FLAG,
  type TaskFormTabsParamList,
} from './utils/taskFormTabsNavigation';

interface TaskFormNavigatorProps {
  task: Task;
  module: ModuleLecturerResponse;
  isReadOnly?: boolean;
  navigationRef: React.RefObject<NavigationContainerRef<TaskFormTabsParamList> | null>;
}

const TabNav = createMaterialTopTabNavigator();

const TaskFormNavigator: React.FC<TaskFormNavigatorProps> = ({
  task,
  module,
  isReadOnly = true,
  navigationRef,
}) => {
  const { t } = useTranslation();
  const { isFeatureActive } = useUserFeatures();
  const gradingRubricEnabled = isFeatureActive(TASK_EDITOR_GRADING_RUBRIC_FEATURE_FLAG);
  const { watch } = useFormContext();
  const taskType = watch('type');
  const isActivity = taskType === 'activity';
  const isEssay = taskType === 'essay';
  const isMCQ = taskType === 'multiple_choice';
  const isOralExam = taskType === 'oral_exam';

  const tabScreenOptions: MaterialTopTabNavigationOptions = {
    tabBarStyle: {
      // @ts-expect-error - using semantic tokens
      backgroundColor: useToken('colors', 'bgApp'),
      elevation: 0,
      shadowOpacity: 0,
      borderBottomWidth: 1,
      // @ts-expect-error - using semantic tokens
      borderBottomColor: useToken('colors', 'borderLight'),
      height: 48,
    },
    tabBarLabelStyle: {
      textTransform: 'none',
      fontSize: 14,
      fontWeight: 'semibold',
    },
    tabBarItemStyle: {
      minWidth: 0,
      width: 'auto',
      paddingHorizontal: 16,
    },
    tabBarIndicatorStyle: {
      // @ts-expect-error - using semantic tokens
      backgroundColor: useToken('colors', 'brandBase'),
      height: 2,
    },
    // @ts-expect-error - using semantic tokens
    tabBarActiveTintColor: useToken('colors', 'textHeading'),
    // @ts-expect-error - using semantic tokens
    tabBarInactiveTintColor: useToken('colors', 'textSubtle'),
    tabBarScrollEnabled: true,
    swipeEnabled: false,
  };

  return (
    <NavigationIndependentTree>
      <NavigationContainer ref={navigationRef}>
        <TabNav.Navigator id="TaskFormNavigator" screenOptions={tabScreenOptions}>
          <TabNav.Screen
            name="Task"
            options={{
              tabBarLabel: t('lecturer.moduleEditor.taskEditor.baseTab'),
              tabBarAccessibilityLabel: 'BaseTaskTab',
            }}
          >
            {() => <TaskBaseForm isReadOnly={isReadOnly} moduleId={module.id} version={module.version} taskId={task.id} />}
          </TabNav.Screen>
          <TabNav.Screen
            name="Resources"
            options={{
              tabBarLabel: t('lecturer.moduleEditor.taskEditor.resourceTab'),
              tabBarAccessibilityLabel: 'ResourcesTab',
            }}
          >
            {() => <TaskResourceForm task={task} module={module} isReadOnly={isReadOnly} />}
          </TabNav.Screen>
          {!isActivity && (
            <TabNav.Screen
              name="Evaluation"
              options={{
                tabBarLabel: t('lecturer.moduleEditor.taskEditor.evalTab'),
                tabBarAccessibilityLabel: 'EvalTab',
              }}
            >
              {() => <TaskEvalForm module={module} isReadOnly={isReadOnly} />}
            </TabNav.Screen>
          )}

          {gradingRubricEnabled && isEssay && (
            <TabNav.Screen
              name="Grading Rubric"
              options={{
                tabBarLabel: t('lecturer.moduleEditor.taskEditor.gradingRubricTab'),
                tabBarAccessibilityLabel: 'GradingRubricTab',
              }}
            >
              {() => (
                <GradingRubricForm
                  isReadOnly={isReadOnly}
                  moduleId={module.id}
                  version={module.version}
                  taskId={task.id}
                />
              )}
            </TabNav.Screen>
          )}

          {isMCQ && (
            <TabNav.Screen
              name="MCQ"
              options={{
                tabBarLabel: t('lecturer.moduleEditor.taskEditor.mcqTab'),
                tabBarAccessibilityLabel: 'MCQTab',
              }}
            >
              {() => {
                if (task.type !== 'multiple_choice') {
                  return <SaveRequiredPlaceholder typeName={t('lecturer.taskTypes.multipleChoice')} />;
                }

                return (
                  <MCQConfigForm
                    isReadOnly={isReadOnly}
                    moduleId={module.id}
                    version={module.version}
                    taskId={task.id}
                    questions={task.mcqConfig?.questions ?? []}
                  />
                );
              }}
            </TabNav.Screen>
          )}

          {isOralExam && (
            <TabNav.Screen
              name="OralExam"
              options={{
                tabBarLabel: t('lecturer.moduleEditor.taskEditor.oralExamTab'),
                tabBarAccessibilityLabel: 'OralExamTab',
              }}
            >
              {() => {
                if (task.type !== 'oral_exam') {
                  return <SaveRequiredPlaceholder typeName={t('lecturer.taskTypes.oralExam')} />;
                }

                return (
                  <OralExamConfigForm
                    isReadOnly={isReadOnly}
                    moduleId={module.id}
                    version={module.version}
                    taskId={task.id}
                    oralExamConfig={task.oralExamConfig}
                  />
                );
              }}
            </TabNav.Screen>
          )}
        </TabNav.Navigator>
      </NavigationContainer>
    </NavigationIndependentTree>
  );
};

export default TaskFormNavigator;
